import type { WorkflowDefinition } from "../types";

/**
 * W8 — Connect earned media to AI visibility.
 *
 * `perTarget` on a coverage group: a campaign is not the property, and an
 * agency reporting three campaigns for one client runs this three times. The
 * group often does not exist when the run starts (building it *is* step one),
 * so the run starts unbound and binds from the step panel once a group exists.
 *
 * The two steps that read the group itself — placements scored inside it, and
 * the group's own PDF export — are `scope: "target"`, which means they stay
 * incomplete until the run says which group it is about. That is deliberate:
 * scoring last quarter's campaign must not check off this quarter's run.
 */
export const earnedMediaWorkflow: WorkflowDefinition = {
	id: "earned-media",
	wNumber: 8,
	title: "Connect earned media to AI visibility",
	outcome: "Show how much of what AI says comes from your earned media work.",
	audiences: ["pr"],
	uses: ["Coverage Groups", "Placements", "AI Citations", "Projects"],
	scope: "property",
	concurrency: "perTarget",
	progress: "explicit",
	target: { kind: "coverageGroup", label: "Coverage group" },
	// Coverage groups are a PR surface: BRAND, PRODUCT, PERSON and CATEGORY all
	// have coverage to group — a category's is the trade press that shapes the
	// market. LOCATION reports through its parent.
	isApplicable: (mode) => mode.showCoverageGroups,
	articleSlug: "how-to-connect-earned-media-to-ai-visibility",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "build-group",
			title: "Group the campaign's placements",
			page: "Coverage Groups",
			route: "/coverage-groups",
			criterion:
				"You've built the group, or chosen the one this workflow is for.",
			why: "A list of links in a spreadsheet cannot be measured against AI answers. A coverage group tracks the whole campaign at once.",
			docsHref: "/docs/dashboards/coverage-groups",
			tasks: [
				"Paste every placement URL, including syndicated copies",
				"Use one group per campaign, or one per month, never both",
				"Name it so the campaign is obvious in an export",
			],
			// Either half satisfies it: a group built for this run, or a run bound
			// to a group that already existed. Another campaign's group sitting on
			// the property does not.
			complete: {
				kind: "any",
				of: [
					{ kind: "since", event: "coverageGroupCreated" },
					{ kind: "target" },
				],
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Coverage groups",
							value: state.property.coverageGroupCount,
							target: 1,
						}
					: null,
		},
		{
			id: "score-placements",
			title: "Score each placement for AI value",
			page: "Coverage Groups",
			route: "/coverage-groups",
			criterion: "You've scored placements in this group.",
			why: "A clip report says the story appeared. This score says whether AI can find that mention and quote it.",
			docsHref: "/docs/methodology/ai-placement-quality-score",
			tasks: [
				"Score the group's placements and read the spread, not the average",
				"Check the publisher value beside each placement score",
				"Note the low scores you can fix next time",
			],
			complete: {
				kind: "since",
				event: "placementScored",
				scope: "target",
			},
			spend: {
				kind: "free",
				note: "Scoring placements is free, however many URLs the group holds.",
			},
			signal: ({ state }) => {
				const inGroup = state.target?.events.placementScored?.count;
				if (inGroup !== undefined) {
					return {
						kind: "count",
						label: "Placements scored in this group",
						value: inGroup,
						target: 1,
					};
				}
				return state.property
					? {
							kind: "count",
							label: "Placements scored so far",
							value: state.property.placements.completed,
						}
					: null;
			},
		},
		{
			id: "citations-page",
			title: "See which placements AI actually cites",
			page: "AI Citations",
			route: "/coverage-groups",
			criterion: "You've read the group's citation funnel.",
			why: "Scoring says which placements are well placed. This says which ones AI used, and that is next quarter's brief.",
			docsHref: "/docs/dashboards/coverage-groups",
			tasks: [
				"Open the group's AI Citations page and read the funnel",
				"Compare the cited placements against the rest",
				"Write down what the cited ones had in common",
			],
			// Nothing observes "you read the funnel". The step exists because
			// skipping it is how a campaign gets repeated identically next
			// quarter.
			complete: { kind: "manual" },
		},
		{
			id: "project-goal",
			title: "Track the campaign's influence over time",
			page: "Projects",
			route: "/projects/new",
			criterion: "You've added the group as a project goal.",
			why: "Citations keep arriving for weeks after your first report. A project goal keeps counting them for you.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Create or open the PR project for this brand",
				"Add the coverage group as a goal",
				"Set the project dates to the campaign's flight",
			],
			complete: {
				kind: "count",
				key: "goalTypes.COVERAGE_GROUP",
				gte: 1,
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Coverage group goals",
							value: state.property.goalTypes.COVERAGE_GROUP,
							target: 1,
						}
					: null,
		},
		{
			id: "export",
			title: "Package the results to share",
			page: "Coverage Groups",
			route: "/coverage-groups",
			criterion: "You've exported this group.",
			why: "The data only does its job once someone outside the team reads it. The export carries your branding, not ours.",
			docsHref: "/docs/dashboards/coverage-groups",
			tasks: [
				"Export the coverage report for the group",
				"Lead with the influence rate, not the clip count",
				"Add one line on what to pitch differently next time",
			],
			complete: {
				kind: "since",
				event: "coverageGroupExported",
				scope: "target",
			},
			signal: ({ state }) =>
				state.target
					? {
							kind: "checklist",
							items: [
								{
									label: "Coverage report exported for this group",
									done: Boolean(
										state.target.events
											.coverageGroupExported?.latestAt,
									),
								},
							],
						}
					: null,
		},
	],
};
